import { useRef, useState } from "react";
import { GiftAnArticleVersion } from "@features/giftAnArticle/utils/types";
import ArticleAppBanner from "@features/toolbar/ArticleToolbar/components/ArticleAppBanner";
import {
  APP_BANNER_CONTENT,
  PODCAST_ARTICLE_TOOLLIST,
  TOOLLIST_ARTICLE,
  TOOLLIST_HEADER,
} from "@features/toolbar/ArticleToolbar/utils/constants";
import { ArticleCommonToolbarProps } from "@features/toolbar/ArticleToolbar/utils/types";
import { useWindowSize } from "@hooks/useWindowSize";
import { cn } from "@util/helpers";

/**
 * The `ArticleToolbar` component provides a toolbar interface for articles,
 * including options for sharing, listening, and interacting with premium features.
 */

export default function ArticleToolbar({
  articleTitle,
  shareUrl,
  testId = "article-toolbar-component",
  isPremium = false,
  handleGenerateGiftURLModal,
  setIsModalOpen,
  alacrityRes,
  keyword,
  displayPurchase,
  cueId = "",
  isFromHeader = false,
  isSummaryAvailable = false,
  isChinese = false,
  articleUrl,
  isPodcastArticleToolbar = false,
  className,
}: ArticleCommonToolbarProps): React.ReactElement {
  const [isAppBannerOpen, setIsAppBannerOpen] = useState(false);
  const [appBannerContent, setAppBannerContent] = useState({
    title: "Download the BT App to enjoy the best reading experience.",
    description: "Download the BT App on your phone.",
  });

  const handleOpenModal = (key: string = "") => {
    const keyValue = key as keyof typeof APP_BANNER_CONTENT;
    const content = APP_BANNER_CONTENT[keyValue];

    if (content) setAppBannerContent(content);

    setIsAppBannerOpen(true);
  };

  const handleCloseModal = () => {
    setIsAppBannerOpen(false);
  };

  const { isScreenLG } = useWindowSize();

  const shareButtonRef = useRef<HTMLButtonElement>(null);

  const handleOpenSharePopover = () => {
    if (!isScreenLG) {
      if (typeof navigator.share === "function" && shareUrl) {
        navigator.share({
          text: articleTitle, // Current page title
          url: shareUrl, // Current page URL
        });
      }

      return;
    }

    shareButtonRef.current?.click();
  };

  const ARTICLE_TOOLLIST = isPodcastArticleToolbar
    ? PODCAST_ARTICLE_TOOLLIST
    : TOOLLIST_ARTICLE;

  const toolList = isFromHeader ? TOOLLIST_HEADER : ARTICLE_TOOLLIST;
  const MAIN_TOOL = toolList.slice(0, 2);
  const SUB_TOOL = toolList.slice(2);

  return (
    <div
      className={cn(
        "flex w-full gap-1 lg:gap-2 print:hidden",
        {
          "mb-4": !isFromHeader,
          "justify-end": isFromHeader,
        },
        className
      )}
      data-testid={testId}
    >
      {MAIN_TOOL.map((tool, index) => {
        const ToolComponent = tool.Component;
        const className = "extraClass" in tool ? tool.extraClass : "";

        // Disable summarise button for Chinese articles
        if (tool.testId === "article-summarise-button-component" && isChinese) {
          return null;
        }

        return (
          <ToolComponent
            articleId={cueId}
            key={`main-tool-${index}`}
            articleTitle={articleTitle || ""}
            shareUrl={shareUrl || ""}
            buttonRef={shareButtonRef}
            icon={tool.icon}
            text={tool.text}
            isPremium={isPremium}
            subshareVersion={GiftAnArticleVersion.version2}
            handleGenerateGiftURLModal={
              handleGenerateGiftURLModal || (() => {})
            }
            setIsModalOpen={setIsModalOpen || (() => {})}
            alacrityRes={alacrityRes}
            handleOpenModal={(key) => {
              handleOpenModal(key || tool.icon);
            }}
            buttonClassname={cn("mr-2", className)}
            testId={tool.testId}
            className={className}
            trackingObject={tool.trackingObject}
            keyword={keyword}
            isSummaryAvailable={isSummaryAvailable}
            isFromHeader={isFromHeader}
            isChinese={isChinese}
            isPodcastArticleToolbar={isPodcastArticleToolbar}
          />
        );
      })}

      {SUB_TOOL.map((tool, index) => {
        const ToolComponent = tool.Component;

        const className = "extraClass" in tool ? tool.extraClass : "";
        const isAlternative = "isAlternative" in tool && tool.isAlternative;

        return (
          <ToolComponent
            articleId={cueId}
            articleUrl={articleUrl}
            key={`sub-tool-${index}`}
            articleTitle={articleTitle || ""}
            shareUrl={shareUrl || ""}
            buttonRef={shareButtonRef}
            icon={tool.icon}
            text={tool.text}
            isPremium={isPremium}
            subshareVersion={GiftAnArticleVersion.version2}
            handleGenerateGiftURLModal={
              handleGenerateGiftURLModal || (() => {})
            }
            setIsModalOpen={setIsModalOpen || (() => {})}
            alacrityRes={alacrityRes}
            handleShareArticle={handleOpenSharePopover}
            keyword={keyword}
            displayPurchase={displayPurchase}
            cueId={cueId}
            handleOpenModal={(key) => {
              handleOpenModal(key || tool.icon);
            }}
            buttonClassname={cn("mr-2", className)}
            testId={tool.testId}
            className={className}
            trackingObject={tool.trackingObject}
            isSummaryAvailable={isSummaryAvailable}
            isFromHeader={isFromHeader}
            isAlternative={isAlternative}
            isChinese={isChinese}
            isPodcastArticleToolbar={isPodcastArticleToolbar}
          />
        );
      })}

      <ArticleAppBanner
        isModalOpen={isAppBannerOpen}
        setIsModalOpen={setIsAppBannerOpen}
        handleCloseModal={handleCloseModal}
        title={appBannerContent.title}
        description={appBannerContent.description}
      />
    </div>
  );
}
